import { Component } from '@theme/component'
import { sectionRenderer } from '@theme/section-renderer'

/**
 * @typedef {Object} FlavorCardRequest
 * @property {URL} pageUrl - Product URL used to render the selected flavor.
 * @property {string} productPath - Normalized path used to match the rendered card.
 */

export class FlavorSwatches extends Component {
  #flavorAbortController = null
  parentProductCard = null

  connectedCallback() {
    super.connectedCallback()
    this.parentProductCard = this.closest('product-card')
    if (!this.parentProductCard) {
      return
    }
    this.addEventListener('click', this.#handleFlavorClick)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleFlavorClick)
    this.#flavorAbortController?.abort()
    super.disconnectedCallback()
  }

  #handleFlavorClick = (event) => {
    const swatch = this.#getClickedSwatch(event)
    if (!swatch) return

    event.preventDefault()
    event.stopPropagation()

    const flavorRequest = this.#buildFlavorRequest(swatch)
    if (!flavorRequest) return

    this.#loadFlavorCard(flavorRequest, swatch)
  }

  #getClickedSwatch(event) {
    if (!(event.target instanceof Element)) return null

    const swatch = event.target.closest('[data-flavor-url]')
    if (!(swatch instanceof HTMLElement && this.contains(swatch))) return null

    return swatch
  }

  #buildFlavorRequest(swatch) {
    const { flavorOptionValueId, flavorUrl } = swatch.dataset
    if (!flavorUrl) return null

    const pageUrl = new URL(flavorUrl, window.location.origin)
    if (flavorOptionValueId) {
      pageUrl.searchParams.set('option_values', flavorOptionValueId)
    }

    return {
      pageUrl,
      productPath: this.#normalizeProductPath(pageUrl)
    }
  }

  async #loadFlavorCard(flavorRequest, swatch) {
    this.#flavorAbortController?.abort()
    const controller = new AbortController()
    this.#flavorAbortController = controller

    this.#setFlavorLoading(swatch, true)

    try {
      const newCard = await this.#resolveFlavorCard(
        flavorRequest,
        controller.signal
      )
      if (controller.signal.aborted) return

      this.#replaceProductCard(newCard, flavorRequest.productPath)
    } catch {
      if (controller.signal.aborted) return
      window.location.href = flavorRequest.pageUrl.toString()
    } finally {
      if (!controller.signal.aborted) {
        this.#setFlavorLoading(swatch, false)
        this.#flavorAbortController = null
      }
    }
  }

  async #resolveFlavorCard(flavorRequest, signal) {
    const fragment = await this.#fetchFlavorFragment(
      flavorRequest.pageUrl,
      signal
    )
    if (signal.aborted) return null

    return this.#findProductCard(fragment, flavorRequest.productPath)
  }

  async #fetchFlavorFragment(pageUrl, signal) {
    const sectionId = this.#resolveSectionId()
    if (!sectionId) {
      return this.#fetchPageFragment(pageUrl, signal)
    }

    const sectionUrl = new URL(pageUrl.toString())
    const sectionHTML = await sectionRenderer.getSectionHTML(
      sectionId,
      false,
      sectionUrl
    )

    return new DOMParser().parseFromString(sectionHTML, 'text/html')
  }

  async #fetchPageFragment(url, signal) {
    if (signal?.aborted) {
      return new DOMParser().parseFromString('', 'text/html')
    }

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      signal
    })
    if (!response.ok) {
      throw new Error(`Failed to load product page: ${response.status}`)
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html')
  }

  #resolveSectionId() {
    return (
      this.dataset.sectionId ||
      this.parentProductCard?.dataset?.sectionId ||
      this.closest('.shopify-section')?.id?.replace('shopify-section-', '') ||
      ''
    )
  }

  #setFlavorLoading(swatch, isLoading) {
    if (!(swatch instanceof HTMLElement)) return
    swatch.classList.toggle('is-loading', isLoading)
    swatch.setAttribute('aria-busy', isLoading.toString())
    swatch.setAttribute('aria-disabled', isLoading.toString())

    if (swatch instanceof HTMLButtonElement) {
      swatch.disabled = isLoading
    }
  }

  #findProductCard(root, productPath) {
    for (const productCard of root.querySelectorAll('product-card')) {
      if (this.#productCardMatchesPath(productCard, productPath)) {
        return productCard
      }
    }

    return null
  }

  #productCardMatchesPath(productCard, productPath) {
    if (this.#matchesProductPath(productCard.dataset.productUrl, productPath)) {
      return true
    }

    for (const link of productCard.querySelectorAll('a[href]')) {
      if (this.#matchesProductPath(link.getAttribute('href'), productPath)) {
        return true
      }
    }

    return false
  }

  #matchesProductPath(value, productPath) {
    if (!value) return false

    try {
      return this.#normalizeProductPath(value) === productPath
    } catch {
      return false
    }
  }

  #normalizeProductPath(value) {
    const url =
      value instanceof URL ? value : new URL(value, window.location.origin)
    return url.pathname.replace(/\/$/, '')
  }

  #replaceProductCard(newCard, productPath) {
    if (!newCard) {
      throw new Error(
        `Product card missing for "${productPath}"; expected matching product-card in render response`
      )
    }
    if (!this.parentProductCard) {
      throw new Error(
        'Parent product-card missing; expected flavor-swatches inside product-card'
      )
    }

    this.parentProductCard.replaceWith(newCard)
  }
}

if (!customElements.get('flavor-swatches')) {
  customElements.define('flavor-swatches', FlavorSwatches)
}
